import {
  Fragment,
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { ListChecks, type LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { Stack } from './Layout';
import { List, ListItem } from './List';
import { Text } from './Text';
import {
  prospectActivityRecipe,
  prospectBoardRecipe,
  prospectDetailRecipe,
  prospectDetailVisibilityRecipe,
  prospectListRecipe,
  prospectListVisibilityRecipe,
  prospectNavRecipe,
  prospectPropertyRecipe,
  prospectPropertyValueRecipe,
  prospectRailRecipe,
  prospectTaskRecipe,
  prospectReviewRecipe,
  prospectPeriodRecipe,
  prospectBarRecipe,
  prospectPaneSectionRecipe,
  prospectRecordBodyRecipe,
  prospectPropertiesRecipe,
  prospectSummaryRecipe,
  prospectCallEntryRecipe,
  prospectFrameRecipe,
  prospectFrameBarRecipe,
  prospectFrameBodyRecipe,
} from '../recipes/prospect-board.recipe';

export function ProspectBoard({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(prospectBoardRecipe(), className)} {...props}>
      {children}
    </div>
  );
}
export const ProspectReviewBanner = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { count: number }
>(function ProspectReviewBanner({ count, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(prospectReviewRecipe(), className)}
      {...props}
    >
      <ListChecks size={16} aria-hidden="true" />
      <Text as="span" variant="body-sm">
        {count} calls to review
      </Text>
    </button>
  );
});
export const ProspectPeriodFilter = forwardRef<
  HTMLSelectElement,
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> & {
    value: string;
    onValueChange: (value: string) => void;
  }
>(function ProspectPeriodFilter(
  { value, onValueChange, className, ...props },
  ref
) {
  return (
    <select
      ref={ref}
      aria-label="Call period"
      className={cn(prospectPeriodRecipe(), className)}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      {...props}
    >
      <option value="today">Today</option>
      <option value="week">This week</option>
      <option value="all">All</option>
    </select>
  );
});
export interface ProspectFilterOption {
  value: string;
  label: string;
}

/**
 * Picks whose calls the board shows. Looks like ProspectPeriodFilter, so the
 * two sit side by side as one row of quiet filters.
 */
export const ProspectRepFilter = forwardRef<
  HTMLSelectElement,
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> & {
    value: string;
    onValueChange: (value: string) => void;
    options: ProspectFilterOption[];
  }
>(function ProspectRepFilter(
  { value, onValueChange, options, className, ...props },
  ref
) {
  return (
    <select
      ref={ref}
      aria-label="Rep"
      className={cn(prospectPeriodRecipe(), className)}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      {...props}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});
export function ProspectRail({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <aside className={cn(prospectRailRecipe(), className)} {...props}>
      {children}
    </aside>
  );
}
export function ProspectListPane({
  children,
  className,
  mobileHidden = false,
  ...props
}: HTMLAttributes<HTMLElement> & { mobileHidden?: boolean }) {
  return (
    <section
      className={cn(
        prospectListRecipe(),
        prospectListVisibilityRecipe({ mobileHidden }),
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
}
export function ProspectDetailPane({
  children,
  className,
  mobileVisible = false,
  ...props
}: HTMLAttributes<HTMLElement> & { mobileVisible?: boolean }) {
  return (
    <section
      className={cn(
        prospectDetailRecipe(),
        prospectDetailVisibilityRecipe({ mobileVisible }),
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
}
export function ProspectNavItem({
  icon: Icon,
  label,
  count,
  selected,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: LucideIcon;
  label: string;
  count: number;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      className={prospectNavRecipe({ selected })}
      aria-current={selected}
      {...props}
    >
      <Icon size={16} />
      <Text variant="body-sm" truncate>
        {label}
      </Text>
      <span className="ml-auto tabular-nums">
        <Text variant="caption" tone="muted">
          {count}
        </Text>
      </span>
    </button>
  );
}
export function ProspectActivityRow({
  children,
  selected,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button
      type="button"
      className={cn(prospectActivityRecipe({ selected }), className)}
      aria-current={selected}
      {...props}
    >
      {children}
    </button>
  );
}
export function ProspectTaskRow({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(prospectTaskRecipe(), className)} {...props}>
      {children}
    </div>
  );
}
export function ProspectPropertyRow({
  icon: Icon,
  label,
  children,
  mobileStack = false,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  mobileStack?: boolean;
}) {
  return (
    <div className={cn(prospectPropertyRecipe({ mobileStack }))}>
      <Icon size={15} className="text-muted-foreground" />
      <Text variant="body-sm" tone="muted">
        {label}
      </Text>
      <Text
        variant="body-sm"
        as="div"
        className={prospectPropertyValueRecipe({ mobileStack })}
      >
        {children}
      </Text>
    </div>
  );
}

/** The bar across the top of a pane. See prospectBarRecipe. */
export function ProspectBar({
  children,
  className,
  sticky = false,
  railHidden = false,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  sticky?: boolean;
  railHidden?: boolean;
}) {
  return (
    <div
      className={cn(prospectBarRecipe({ sticky, railHidden }), className)}
      {...props}
    >
      {children}
    </div>
  );
}

/** A block of the list or detail pane, inset by what it holds. */
export function ProspectPaneSection({
  children,
  className,
  inset = 'body',
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  inset?: 'title' | 'label' | 'group' | 'banner' | 'body' | 'detail';
}) {
  return (
    <div
      className={cn(prospectPaneSectionRecipe({ inset }), className)}
      {...props}
    >
      {children}
    </div>
  );
}

/** The scrolling body of an open prospect, under its bar. */
export function ProspectRecordBody({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(prospectRecordBodyRecipe(), className)} {...props}>
      {children}
    </div>
  );
}

/** The property rows of an open prospect, ruled off from what follows. */
export function ProspectProperties({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(prospectPropertiesRecipe(), className)} {...props}>
      {children}
    </div>
  );
}

/** A run of summary text, bold where the summary marked it. */
export type ProspectSummaryRun = { text: string; strong: boolean };

/**
 * A call summary, one point per line. `bulleted` draws the points as a
 * list; otherwise each point is its own paragraph.
 *
 *   <ProspectSummary bulleted points={[[{ text: 'Out on a job', strong: false }]]} />
 */
export function ProspectSummary({
  points,
  bulleted = false,
  tone = 'default',
  className,
}: {
  points: ProspectSummaryRun[][];
  bulleted?: boolean;
  tone?: 'default' | 'muted';
  className?: string;
}) {
  const line = (runs: ProspectSummaryRun[]) =>
    runs.map((run, index) =>
      run.strong ? (
        <Text key={index} as="strong" variant="inherit" tone="default">
          {run.text}
        </Text>
      ) : (
        <Fragment key={index}>{run.text}</Fragment>
      )
    );
  return (
    <div className={cn(prospectSummaryRecipe(), className)}>
      {bulleted ? (
        <List gap={2} tone={tone}>
          {points.map((runs, index) => (
            <ListItem key={index}>{line(runs)}</ListItem>
          ))}
        </List>
      ) : (
        <Stack gap={3}>
          {points.map((runs, index) => (
            <Text key={index} variant="body-sm" tone={tone}>
              {line(runs)}
            </Text>
          ))}
        </Stack>
      )}
    </div>
  );
}

/** One earlier call in the open prospect. Sits in a `<Stack divided>`. */
export function ProspectCallEntry({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(prospectCallEntryRecipe(), className)} {...props}>
      {children}
    </div>
  );
}

/**
 * The board as a page of its own, with a slim bar across the top. `bar`
 * holds the title on the left and whatever goes on the right.
 *
 *   <ProspectBoardFrame bar={<>…</>}><ProspectBoard>…</ProspectBoard></ProspectBoardFrame>
 */
export function ProspectBoardFrame({
  bar,
  fill = 'screen',
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  bar: ReactNode;
  fill?: 'screen' | 'parent';
}) {
  return (
    <div className={cn(prospectFrameRecipe({ fill }), className)} {...props}>
      <header className={prospectFrameBarRecipe()}>{bar}</header>
      <div className={prospectFrameBodyRecipe()}>{children}</div>
    </div>
  );
}
